import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { UseQueryResult, UseMutationResult } from '@tanstack/react-query';

import type { CollectionResponse } from 'api/market-view-api';
import {
  createReport,
  deleteReport,
  getReports,
  updateReport,
} from 'api/generated/report-controller/report-controller';
import type { ReportDto } from 'api/generated/schemas/reportDto';
import type { ReportRequest } from 'api/generated/schemas/reportRequest';
import type { GetReportsParams } from 'api/generated/schemas/getReportsParams';
import { parsePaginationHeaders } from 'helpers/api/parse-pagination-headers';
import { toQueryParamsObject } from 'helpers/api/to-query-params-object';
import type {
  SortDirection,
  FilterValue,
} from 'helpers/api/build-query-params';
import { useCurrentUser } from 'hooks/api/use-current-user';

export const adminReportsKeys = {
  all: ['admin-reports'] as const,
  lists: () => [...adminReportsKeys.all, 'list'] as const,
  list: (params?: AdminReportsListParams) =>
    [...adminReportsKeys.lists(), params] as const,
};

export interface AdminReportsListParams {
  pageSize?: number;
  pageIndex?: number;
  sortBy?: SortDirection[];
  filters?: Record<string, FilterValue> | null;
}

interface UseAdminReportsListArgs extends AdminReportsListParams {
  enabled?: boolean;
}

export const useAdminReportsList = ({
  pageSize = 10,
  pageIndex = 0,
  sortBy = [],
  filters,
  enabled = true,
}: UseAdminReportsListArgs): UseQueryResult<
  CollectionResponse<ReportDto[]>,
  Error
> => {
  const user = useCurrentUser();
  const userId = (user?.data?.oktaUserId ?? '') as string;

  return useQuery({
    queryKey: adminReportsKeys.list({ pageSize, pageIndex, sortBy, filters }),
    queryFn: async () => {
      const response = await getReports(
        userId,
        toQueryParamsObject({
          pageSize,
          pageIndex,
          sortBy,
          filters,
        }) as GetReportsParams
      );

      return {
        data: response.data,
        pagination: parsePaginationHeaders(response.headers, {
          pageIndex,
          pageSize,
        }),
      };
    },
    enabled: enabled && !!userId,
    placeholderData: (previousData) => previousData,
  });
};

interface CreateReportVariables {
  userId: string;
  reportData: ReportRequest;
}

export const useCreateReport = (): UseMutationResult<
  ReportDto,
  Error,
  CreateReportVariables
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, reportData }: CreateReportVariables) =>
      createReport(userId, reportData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminReportsKeys.lists() });
    },
  });
};

interface ModifyReportVariables {
  userId: string;
  reportId: string | number;
  reportData: ReportRequest;
}

export const useModifyReport = (): UseMutationResult<
  ReportDto,
  Error,
  ModifyReportVariables
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, reportId, reportData }: ModifyReportVariables) =>
      updateReport(userId, Number(reportId), reportData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminReportsKeys.lists() });
    },
  });
};

interface DeleteReportVariables {
  userId: string;
  reportId: string | number;
}

export const useDeleteReport = (): UseMutationResult<
  void,
  Error,
  DeleteReportVariables
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, reportId }: DeleteReportVariables) =>
      deleteReport(userId, Number(reportId)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminReportsKeys.lists() });
    },
  });
};
